import { BrowseHandler } from './BrowseHandler.js';
import { DragHandler } from './DragHandler.js';
import { DropHandler } from './DropHandler.js';
import { PluploadFile } from './File.js';
import { XhrHandler } from './XhrHandler.js';
import constants from './constants.js';

/**
 * Registered file filters
 * @type {Object}
 * @private
 */
const _filters = {};

/**
 * Unique number for uploader id
 * @type {Number}
 * @private
 */
let _idx = 0;

/**
 * Class to create a new upload widget
 * @class plupload.Uploader
 */
export class PluploadUploader {
	/**
	 * Register a filter that can be specified to allow or disallow a file from being added
	 * @param {String} filterName  The name of the filter
	 * @param {Function} filterFunction  A function that receives the configuration value,
	 *   the File object and a callback to invoke with true if the filter allows the file
	 * @static
	 */
	static addFileFilter(filterName, filterFunction) {
		_filters[filterName] = filterFunction;
	}

	/**
	 * Create a widget with the given options
	 * @param {Object} options
	 * @property {HTMLElement} [browse_button]  An element to make into the "choose files" button
	 * @property {HTMLElement} [drop_element]  An element to make into a drop zone for adding files
	 * @property {String} url  The URL to send the file payload
	 * @property {Object} multipart_params  Key-value pairs to send with the POST payload
	 * @property {Boolean} [multi_selection=false]  If true, allow choosing/dropping multiple files
	 * @property {Object} [filters]  Name/value pairs of filters and their option values
	 * @property {String|Number} [filters.max_file_size]  The max file size in bytes or a string with units such as "25MB"
	 * @property {Boolean} [filters.prevent_duplicates=false]  True to prevent the same file being added twice
	 * @property {Boolean} [filters.prevent_empty=false]  True to prevent empty files being added
	 */
	constructor(options) {
		this.id = ++_idx;
		this.unbindAll();
		this.files = [];
		this.state = constants.STOPPED;
		this.features = {
			dragdrop: true,
			chunks: false,
			jpgresize: false,
			pngresize: false,
		};
		this.runtime = 'html5';
		this.dropHandler = new DropHandler({
			onsuccess: filesToAdd => {
				if (filesToAdd.length === 0) {
					return;
				}
				if (this.settings.multi_selection) {
					this.addFiles(filesToAdd);
				} else {
					this.addFile(filesToAdd[0]);
				}
			},
			onerror: error => {
				this.trigger('Error', {
					code: constants.IO_ERROR,
					message: `File read error: ${error}`,
					file: undefined,
				});
			},
		});
		this.setOption(options);
	}

	/**
	 * Initialize the drop zone and browse button
	 */
	init() {
		this.trigger('Init');
		this._teardownDropElement();
		this._setupDropZone();
		this._teardownBrowseButton();
		this._setupBrowseButton();
		this.trigger('PostInit');
	}

	/**
	 * Cancel all uploads
	 */
	stop() {
		// abort all files that may be in process
		this.files.forEach(pluploadFile => {
			pluploadFile.cancel();
		});
		this.files = [];
		this.trigger('CancelUpload');
	}

	/**
	 * Destroy the widget and DOM listeners
	 */
	destroy() {
		this.stop();
		this._teardownDropElement();
		this._teardownBrowseButton();
		this.trigger('Destroy');
	}

	/**
	 * Set one or all widget options
	 * @param {String|Object} name  The widget option name (see contstructor docs) or an object with name-value pairs
	 * @param {*} [value]  The value for the given option
	 */
	setOption(name, value) {
		if (arguments.length === 1) {
			this.settings = name;
		} else {
			this.settings[name] = value;
		}
	}

	/**
	 * Get the widget option
	 * @param {String} name
	 * @returns {*}
	 */
	getOption(name) {
		if (arguments.length === 1) {
			return this.settings[name];
		}
		return this.settings;
	}

	/**
	 * Present only for compatibility with plupload
	 */
	refresh() {}

	/**
	 * Add an event listener
	 * @param {String} types  The event(s) name to respond to (space delimited)
	 * @param {Function} handler  The callback. Receives this uploader instance as argument one
	 *   And a payload as argument two. Payload for the Error event is an error object with {code, message, file}.
	 *   Payload for other events is the plupload.File object
	 */
	bind(types, handler) {
		types.split(/\s+/).forEach(type => {
			this._listeners.push({ type, handler });
		});
	}

	/**
	 * Remove the given event listener
	 * @param {String} types  The event(s) name to respond to (space delimited)
	 * @param {Function} handler  The callback.
	 */
	unbind(types, handler) {
		const newListeners = [];
		types.split(/\s+/).forEach(type => {
			this._listeners.forEach(listener => {
				if (listener.type !== type || listener.handler !== handler) {
					newListeners.push(listener);
				}
			});
		});
		this._listeners = newListeners;
	}

	/**
	 * Remove all event listeners
	 */
	unbindAll() {
		this._listeners = [];
		this.bind('FilesAdded FilesRemoved', () => {
			this.trigger('QueueChanged');
		});
	}

	/**
	 * Trigger the given event with the given payload
	 * @param {String} notifyType  The event name
	 * @param {*} [payload={}]  The payload to send to handlers
	 */
	trigger(notifyType, payload = {}) {
		this._listeners.forEach(({ type, handler }) => {
			if (notifyType === type) {
				handler(this, payload);
			}
		});
	}

	/**
	 * Add a list of native File objects to the upload queue
	 * @param {File[]} listOfNativeFiles  Files from a DOM drop event
	 */
	addFiles(listOfNativeFiles) {
		const currNumFiles = this.files.length;
		const arrayOfNativeFiles = [].slice.call(listOfNativeFiles);
		const next = () => {
			if (arrayOfNativeFiles.length === 0) {
				const newNumFiles = this.files.length;
				if (newNumFiles > currNumFiles) {
					const newPluploadFiles = this.files.slice(currNumFiles);
					this.trigger('FilesAdded', newPluploadFiles);
				}
			} else {
				this._initFile(arrayOfNativeFiles.shift(), next);
			}
		};
		next();
	}

	/**
	 * Add a single file to the upload queue
	 * @param {File} nativeFile
	 */
	addFile(nativeFile) {
		this._initFile(nativeFile, pluploadFile => {
			this.trigger('FilesAdded', [pluploadFile]);
		});
	}

	/**
	 * Remove the given file from the queue
	 * @param {plupload.File} pluploadFile
	 */
	removeFile(pluploadFile) {
		const idx = this.files.indexOf(pluploadFile);
		if (idx > -1) {
			this.files.splice(idx, 1);
		}
	}

	/**
	 * Begin uploading the files in the queue
	 */
	start() {
		const numThreads = this.settings.num_threads || 2;
		const inProgress = this.files.filter(file => file.status === constants.UPLOADING);
		if (inProgress.length >= numThreads) {
			// already uploading max threads
			return;
		}
		const inQueue = this.files.filter(file => file.status === constants.QUEUED);
		let numToAdd = numThreads - inProgress.length;
		while (numToAdd-- > 0 && inQueue.length) {
			this._post(inQueue.shift());
		}
	}

	/**
	 * Apply filters and create a plupload.File object for the given File object
	 * @param {File} nativeFile  A File object
	 * @param {Function} onComplete  Function that will be called if all filters pass
	 * @private
	 */
	_initFile(nativeFile, onComplete) {
		if (!this.settings.multi_selection) {
			this.files.forEach(pluploadFile => {
				pluploadFile.cancel();
			});
			this.files = [];
		}
		const pluploadFile = new PluploadFile(nativeFile);
		const filterNames = Object.keys(this.settings.filters || {});
		// use next function concept to allow each filter to run asynchronously
		const next = isAllowed => {
			if (!isAllowed) {
				this.trigger('FileFiltered', pluploadFile);
				return;
			}
			if (filterNames.length === 0) {
				this.files.push(pluploadFile);
				onComplete(pluploadFile);
			} else {
				const filterName = filterNames.shift();
				const value = this.settings.filters[filterName];
				const handler = _filters[filterName];
				if (handler) {
					handler.call(this, value, pluploadFile, next);
				} else {
					console.warn(`UploadManager unknown filter "${filterName}" skipped.`);
					next(true);
				}
			}
		};
		next(true);
	}

	/**
	 * Send the file in a POST request to S3
	 * @param {plupload.File} pluploadFile  The file to post
	 * @private
	 */
	_post(pluploadFile) {
		this.trigger('BeforeUpload', pluploadFile);
		const xhr = new XhrHandler({
			file: pluploadFile,
			url: this.settings.url,
			multipart_params: this.settings.multipart_params,
			onerror: message => {
				this._maybeMarkAsStopped();
				this.trigger('Error', {
					code: constants.HTTP_ERROR,
					message,
					file: pluploadFile,
				});
				this.start();
			},
			onprogress: () => {
				this.trigger('UploadProgress', pluploadFile);
				this.start();
			},
			oncomplete: () => {
				this.trigger('FileUploaded', pluploadFile);
				this.start();
			},
		});
		xhr.send();
		pluploadFile.status = constants.UPLOADING;
		this._setState(constants.STARTED);
	}

	/**
	 * An object like the QueueProgress object containing totals
	 * @see http://www.plupload.com/docs/v2/QueueProgress
	 * @returns {Object}
	 */
	get total() {
		let progress = {
			loaded: 0,
			size: 0,
			uploaded: 0,
			failed: 0,
			queued: 0,
			percent: 0,
			bytesPerSec: 0,
		};
		this.files.forEach(pluploadFile => {
			progress.loaded += pluploadFile.loaded || 0;
			progress.size += pluploadFile.size || 0;
			progress.uploaded += pluploadFile.status === constants.DONE ? 1 : 0;
			progress.failed += pluploadFile.status === constants.FAILED ? 1 : 0;
			progress.queued += pluploadFile.status === constants.QUEUED ? 1 : 0;
		});
		progress.percent = progress.size ? Math.ceil((progress.loaded / progress.size) * 100) : 0;
		return progress;
	}

	/**
	 * Set the state and fire the StateChanged event
	 * @param {Number} state  One of the constants for uploader state
	 * @private
	 */
	_setState(state) {
		this.state = state;
		this.trigger('StateChanged');
	}

	/**
	 * Mark status as stopped if no files are in progress
	 * @private
	 */
	_maybeMarkAsStopped() {
		if (!this._hasAnyInProgress()) {
			this._setState(constants.STOPPED);
		}
	}

	/**
	 * Check if any files are STARTED or UPLOADING (not DONE or FAILED)
	 * @returns {Boolean}  True if any are in progress
	 * @private
	 */
	_hasAnyInProgress() {
		return this.files.some(pluploadFile => {
			return pluploadFile.status === constants.STARTED || pluploadFile.status === constants.UPLOADING;
		});
	}

	/**
	 * Setup the drop element to receive dragged files
	 * @private
	 */
	_setupDropZone() {
		let element = this.settings.drop_element;
		if (typeof element == 'string') {
			element = document.getElementById(element);
		}
		if (!element) {
			return;
		}
		this.dragHandler = new DragHandler(element, {
			hoverClassName: 'su-hover',
			ondragenter: evt => this.trigger('DragEnter', evt),
			ondragleave: evt => this.trigger('DragLeave', evt),
			ondrop: evt => {
				this.trigger('Drop', evt);
				this.dropHandler.process(evt);
			},
		});
	}

	/**
	 * Remove listeners from the drop zone
	 * @private
	 */
	_teardownDropElement() {
		if (this.dragHandler) {
			this.dragHandler.teardown();
		}
	}

	/**
	 * Setup the browse button for picking file(s) from an <input type="file"> element
	 * @private
	 */
	_setupBrowseButton() {
		let element = this.settings.browse_button;
		if (typeof element == 'string') {
			element = document.getElementById(element);
		}
		if (!element) {
			return;
		}
		this.browseHandler = new BrowseHandler(element, {
			multi_selection: this.settings.multi_selection,
			onpick: files => this.addFiles(files),
		});
	}

	/**
	 * Remove <input type="file"> element
	 * @private
	 */
	_teardownBrowseButton() {
		if (this.browseHandler) {
			this.browseHandler.teardown();
		}
	}
}
